import React, { memo } from 'react';
import { Input, Rate, Select } from 'antd';
import { shallowEqual, useSelector } from 'react-redux';
import PhoneNumberInput from '../../../../../../components/phone-input';
import { PriorityList } from '../../../../../../components/requestComponents';
import { dspxGetChildrens } from '../../../../../../helpers/dspx-utils';
import { ReactComponent as AdditionalInfoIcon } from '../../../../../../resource/AdditionalInfoIcon.svg';
import {
  AttachIcon,
  BadWorkIcon,
  NoAttachIcon,
  RetryIcon,
  ToolIcon,
  WaitingIcon,
} from '../../../../../../resource/icons';
import normalizeApplicantPhone from '../../../lib/normalizeApplicantPhone';
import {
  hasFilterValue,
  selectAdditionalActiveCount,
} from '../../../model/activity';
import { useRequestFilterCommands } from '../../../model/commandsContext';
import { requestFilterSectionIds } from '../../../model/sectionExpansion';
import { selectCreationMethods } from '../../../model/selectors';
import { useFilterChange } from '../../../model/useFilterChange';
import FilterField from '../../primitives/FilterField';
import FlagControl from '../../primitives/FlagControl';
import FilterSection from '../../primitives/FilterSection';
import { multiSelectTagProps } from '../../primitives/selectTagProps';
import styles from './AdditionalInfoFilterSection.module.scss';

const { Option } = Select;

const selectAdditionalFilterState = (state) => {
  const filter = state.filterStore;

  return {
    valueMethodCreatingRequest: filter.valueMethodCreatingRequest,
    valuePriorityRequest: filter.valuePriorityRequest,
    valuePhone: filter.valuePhone,
    valueRating: filter.valueRating,
    retry: filter.retry,
    internal: filter.internal,
    badWork: filter.badWork,
    waiting: filter.waiting,
    attachmentFilter: filter.attachmentFilter,
    valueClientName: filter.valueClientName,
  };
};

const AdditionalInfoFilterSection = () => {
  const filterCommands = useRequestFilterCommands();
  const filter = useSelector(selectAdditionalFilterState, shallowEqual);
  const creationMethods = useSelector(selectCreationMethods);
  const activeCount = useSelector(selectAdditionalActiveCount);
  const { changeFilter } = useFilterChange();

  const handlePhonePaste = (event) => {
    const phoneNumber = normalizeApplicantPhone(
      event.clipboardData?.getData('text')
    );

    if (!phoneNumber) return;

    event.preventDefault();
    changeFilter(filterCommands.applyPhone(phoneNumber));
  };

  return (
    <FilterSection
      sectionId={requestFilterSectionIds.additionalInfo}
      heading="Дополнительная информация"
      description="Данные заявителя, способ создания и признаки"
      icon={<AdditionalInfoIcon />}
      activeCount={activeCount}
    >
      <div className={styles.grid}>
        <FilterField
          label="Телефон заявителя"
          active={hasFilterValue(filter.valuePhone)}
        >
          <PhoneNumberInput
            addonBefore="+7"
            value={filter.valuePhone}
            placeholder="Телефон"
            allowClear={true}
            onPaste={handlePhonePaste}
            onChange={(value) => {
              changeFilter(filterCommands.applyPhone(value));
            }}
          />
        </FilterField>

        <FilterField label="Оценка" active={hasFilterValue(filter.valueRating)}>
          <Select
            {...multiSelectTagProps}
            mode="multiple"
            allowClear={true}
            placeholder="Выберите оценку"
            notFoundContent="нет данных"
            optionLabelProp="tagg"
            value={filter.valueRating}
            onChange={(value) => {
              changeFilter(filterCommands.applyRating(value));
            }}
          >
            <Option key={6} tagg="б/о">
              Без оценки
            </Option>
            {[1, 2, 3, 4, 5].map((rating) => (
              <Option key={rating} tagg={rating}>
                <Rate disabled defaultValue={rating} style={{ fontSize: 10 }} />
              </Option>
            ))}
          </Select>
        </FilterField>

        <FilterField
          label="Ф.И.О. заявителя"
          active={hasFilterValue(filter.valueClientName)}
        >
          <Input
            value={filter.valueClientName}
            placeholder="ФИО"
            allowClear={true}
            onChange={(e) => {
              changeFilter(filterCommands.applyClientName(e.target.value));
            }}
          />
        </FilterField>

        <PriorityList
          render={({ priorities }) => (
            <FilterField
              label="Приоритет"
              active={hasFilterValue(filter.valuePriorityRequest)}
            >
              <Select
                {...multiSelectTagProps}
                mode="multiple"
                defaultActiveFirstOption={false}
                allowClear={true}
                placeholder="Выберите приоритет"
                value={
                  priorities?.length > 0 ? filter.valuePriorityRequest : []
                }
                onChange={(value) => {
                  changeFilter(filterCommands.applyPrioritiesRequest(value));
                }}
                optionLabelProp="label"
                optionFilterProp="searchtext"
              >
                {priorities?.map((priority) => (
                  <Option
                    key={priority.id}
                    searchtext={priority.name}
                    label={`${priority.code} - ${priority.name}`}
                    value={priority.id}
                  >
                    {priority.code} - {priority.name}
                  </Option>
                ))}
              </Select>
            </FilterField>
          )}
        />

        <FilterField
          label="Способ создания"
          active={hasFilterValue(filter.valueMethodCreatingRequest)}
          className={styles.fullWidth}
        >
          <Select
            {...multiSelectTagProps}
            mode="multiple"
            defaultActiveFirstOption={false}
            allowClear={true}
            placeholder="Выберите способ"
            value={filter.valueMethodCreatingRequest}
            onChange={(value) => {
              changeFilter(filterCommands.applyMethodCreatingRequest(value));
            }}
            optionLabelProp="label"
            optionFilterProp="searchtext"
          >
            {dspxGetChildrens(creationMethods)}
          </Select>
        </FilterField>

        <div className={styles.flags}>
          <FlagControl
            active={filter.retry}
            icon={<RetryIcon />}
            onChange={(value) => {
              changeFilter(filterCommands.applyRetry(value));
            }}
          >
            Повторная
          </FlagControl>

          <FlagControl
            active={filter.internal}
            icon={<ToolIcon />}
            onChange={(value) => {
              changeFilter(filterCommands.applyInternal(value));
            }}
          >
            Внутренняя
          </FlagControl>

          <FlagControl
            active={filter.badWork}
            icon={<BadWorkIcon />}
            onChange={(value) => {
              changeFilter(filterCommands.applyBadWork(value));
            }}
          >
            Для обсуждения
          </FlagControl>

          <FlagControl
            active={filter.waiting}
            icon={<WaitingIcon grey={!filter.waiting} />}
            onChange={(value) => {
              changeFilter(filterCommands.applyWaiting(value));
            }}
          >
            Ожидание
          </FlagControl>

          <FlagControl
            active={filter.attachmentFilter === 'true'}
            icon={<AttachIcon />}
            onChange={(value) => {
              changeFilter(
                filterCommands.applyAttachmentFilter(value ? 'true' : null)
              );
            }}
          >
            С вложениями
          </FlagControl>

          <FlagControl
            active={filter.attachmentFilter === 'false'}
            icon={<NoAttachIcon />}
            onChange={(value) => {
              changeFilter(
                filterCommands.applyAttachmentFilter(value ? 'false' : null)
              );
            }}
          >
            Без вложений
          </FlagControl>
        </div>
      </div>
    </FilterSection>
  );
};

export default memo(AdditionalInfoFilterSection);
